import React, { useContext, useState } from "react";
import { NotificationsContext } from "../../ui-components/Notifications";
import sharedApiRequest from "../../services/apiRequest";
import apiRequest from "../../services/apiRequest";
import CountryContext from "../../CountryContext";

import EmailUserInvitationModal from "../Modals/EmailUserInvitationModal";
import { OPTIONS, LOCALIZATION, getColumns } from "./Params/Connections";
import {createFilterParams, createOrderingParams} from "./utils";
import { tableIcons } from "../../ui-components/TableIcons";
import MaterialTable from "@material-table/core";
import TableStyle from "../TableStyle";
import _ from "lodash";
import {MergeRequestModal} from "@feature/users/connections";
import {useMergeAccounts} from "@feature/users/hooks/useMergeAccounts";


function ConnectionsTable({
  setTotal,
  sales,
  fields,
  roles,
  intl,
  perm
}) {

  // context
  const { country } = useContext(CountryContext);
  const { push } = useContext(NotificationsContext);
  const internalRoles = ['admin', 'CS', 'sales', 'field', 'area_manager', 'agent', 'sales_director']
  const _roles = roles.filter(r => !internalRoles.includes(r.name) && r.name !== 'contact_user')
    .reduce((obj, i) => _.set(obj, [i.id], intl.formatMessage({ id: i.name })), {});

  // hooks
  const {sendRequest: sendMergeRequest} = useMergeAccounts();

  // state
  const ref = React.useRef(null);
  const [connections, setConnections] = useState();
  const [columns, setColumns] = useState([]);
  const [invitation, setInvitation] = useState(null);
  const [mergeableUserData, setMergeableUserData] = useState(null);

  // utilities
  const invitee = !!invitation
    ? connections?.[invitation.id]
    : null;

  const sendInvitation = async (email) => {
    try {
      // call API
      await sharedApiRequest.post(`/users/invitate`, {
        id: invitation.id,
        email: email,
        country: country.code,
        role_id: connections[invitation.id].role_id,
      });
      push({type: "success", title: intl.formatMessage({ id: "ok_invited" })});

      // refresh table
      ref.current?.onQueryChange();

      // close modal if no errors occurred
      setInvitation(null);
    }
    catch (err) {
      push({type: "error", title: intl.formatMessage({id: "an_error_occurred"})});
      throw err;
    }
  };

  return (
    <>
      <TableStyle>
        <MaterialTable
          tableRef={ref}
          columns={columns}
          data={query =>
            new Promise((resolve, reject) => {
              const payload = {
                country_code: country.code,
                perm,
                filters: JSON.parse(createFilterParams(query) || null) ,
                ordering: JSON.parse(createOrderingParams(query) || null),
                limit: query.pageSize,
                offset: query.page * query.pageSize,
              };
              // we need to fix filters, adjusting it for contact user
              for (let filter of payload.filters || []){
                if (filter.column === "role_id") {
                  let filter_fixed = {
                    logic: "or",
                    filters: [
                      {
                        value: filter.value,
                        operator: "in",
                        column: "role_id",
                      },
                      {
                        value: filter.value,
                        operator: "in",
                        column: "id",
                        model: "account_user_role",
                      },
                    ],
                  }
                  payload.filters = payload.filters.filter(f => f.column !== "role_id")
                  payload.filters.push(filter_fixed)
                }
                if (filter.column === 'full_name') {
                  let filter_fixed = {
                    ...filter,
                    also_on_account_user: true
                  }
                  payload.filters = payload.filters.filter(f => f.column !== "full_name")
                  payload.filters.push(filter_fixed)
                }
              }
              return apiRequest
                .post("/connections", payload)
                .then((result) => {
                  if (!columns.length) {
                    setColumns(getColumns(intl, _roles, sales, fields, setInvitation));
                  }
                  setTotal(result["total"]);
                  // setData(result["items"]);
                  setConnections((result["items"] || []).reduce((obj, i) => _.set(obj, [i.id], i), {}))
                  resolve({
                    data: result["items"],
                    page: query.page,
                    totalCount: result["total"],
                  });
                })
                .catch(() => {
                  push({
                    title: "Errore durante il caricamento",
                    type: "error",
                  });
                  reject();
                });
              }
            )
          }
          icons={tableIcons}
          // isLoading={!connections}
          options={OPTIONS}
          localization={LOCALIZATION}
        />
      </TableStyle>
      {!!invitation && (
        <EmailUserInvitationModal
          intl={intl}
          inviteeFullName={invitee.full_name}
          inviteeEmail={invitee?.pending_invitations?.[0]?.user_email}
          onClose={() => {
            setInvitation(null);
          }}
          onConfirm={async (email, isMerge) => {
            if (isMerge) {
              // CASE A: email was found among the existing users --> Open modal to ask if user wants to send a merge request
              setInvitation(null);
              setMergeableUserData({
                id: invitation.id,
                name: invitee.full_name,
                email: email,
                role_name: invitation.role_name
              });
            }
            else {
              // CASE B: email is not among the existing users --> Send invitation
              await sendInvitation(email);
            }
          }}
        />
      )}
      {mergeableUserData && (
        <MergeRequestModal
          name={mergeableUserData.name}
          email={mergeableUserData.email}
          onClose={() => { setMergeableUserData(null); }}
          onConfirm={async () => {
            try {
              await sendMergeRequest(mergeableUserData.email, mergeableUserData.id, mergeableUserData.role_name);
              push({type: "success", title: intl.formatMessage({id: "request_sent"})});
            } catch (err) {
              push({type: "error", title: intl.formatMessage({id: err?.response?.data?.detail})});
            }

            // const response = await sendMergeRequest(mergeableUserData.email, mergeableUserData.id, mergeableUserData.role_name);
            // if (response.success){
            //   push({type: "success", title: intl.formatMessage({id: "request_sent"})});
            //   setMergeableUserData(null);
            // } else {
            //   push({type: "error", title: intl.formatMessage({id: response.failure_reason})});
            // }
          }}
        />
      )}
    </>
  );
}

export default React.memo(ConnectionsTable, (props, nextProps) => {
  // Render component only when users changes
  return (
    _.isEqual(props.sales, nextProps.sales) &&
    _.isEqual(props.fields, nextProps.fields)
  );
});
